import { useState } from 'react';
import {
  Banner,
  Button,
  Chip,
  EmptyState,
  Modal,
  SectionHead,
  Spinner,
  Switch,
  TextField,
  Toast
} from '../components/primitives';
import { SettingsBackLink } from '../components/settings/SettingsBackLink';
import type { InstalledSkill, SkillApprovalStatus } from '../lib/models/skills';
import { useSkillsRegistryVM } from '../lib/vm/useSkillsRegistryVM';
import './SettingsSkills.css';

const LEDE =
  'Install a skill from a GitHub repo, pinned to a version. Installed skills are governed by your org and can be toggled on or off. (Installing registers the skill; running it is enabled separately.)';

const BLOCKED_TOGGLE_MSG =
  "Blocked by org policy — a member's toggle can't override an org block.";

// Slice 1.3 — capability-gated reasons (never a silent failure).
const INSTALL_PERM_MSG = 'Blocked by org policy — needs the Install skills permission.';

/** Approval-state chip (only shown when the allowlist is on). */
function ApprovalChip({ status }: { status: SkillApprovalStatus }) {
  if (status === 'approved') return <Chip variant="success">approved</Chip>;
  if (status === 'blocked') return <Chip variant="blocked">blocked</Chip>;
  return <Chip variant="warning">pending</Chip>;
}

/**
 * `/settings/skills`.
 *
 * The org skills registry + governance (Skills 1.2). An install form feeds
 * `add` (which becomes install / install&approve / request depending on the
 * allowlist posture + the `canCurate` seam). Below it: an admin governance
 * posture card (the master switch), a member-request Requested review queue
 * (only when the allowlist is on), and the installed list — where an
 * unapproved skill renders blocked-by-org-policy (greyed, disabled toggle,
 * stated reason) rather than failing silently. All chrome comes from
 * primitives; the page CSS is layout/state-only.
 */
export function SettingsSkills() {
  const vm = useSkillsRegistryVM();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [pendingRemove, setPendingRemove] = useState<InstalledSkill | null>(null);
  // Page-local toast for the anti-silent-failure block message (a UI-only event).
  const [blockToast, setBlockToast] = useState<string | null>(null);

  const { allowlistEnabled, canInstall, canCurate, capsLoading } = vm;

  // The add action becomes a member "request" only when the allowlist is on and
  // the viewer can't curate — that path is membership-gated (any member may
  // request), so it does NOT require skills.install. Every other path (install /
  // install&approve) goes through install_skill and needs skills.install.
  const requestPath = allowlistEnabled && !canCurate;
  const canSubmitAdd = requestPath ? true : canInstall;

  const addLabel = !allowlistEnabled ? 'Add' : canCurate ? 'Add & approve' : 'Request';
  const addNote = !allowlistEnabled
    ? 'Added skills are usable immediately — no approval needed.'
    : canCurate
      ? 'You add and approve what your org may use.'
      : "Your add becomes a request an admin reviews before it's usable.";
  const busyLabel = requestPath ? 'Requesting…' : 'Adding…';

  const onAdd = async () => {
    const ok = await vm.actions.add({ url, name, version });
    if (ok) {
      setUrl('');
      setName('');
      setVersion('');
    }
  };

  // The block message takes precedence so a lingering VM toast can't mask the
  // anti-silent-failure explanation. It auto-clears on dismiss.
  const toast = blockToast ?? vm.toast;
  const dismissToast = () => {
    vm.actions.clearToast();
    setBlockToast(null);
  };
  const flagBlocked = () => {
    vm.actions.clearToast();
    setBlockToast(BLOCKED_TOGGLE_MSG);
  };
  const flagPermBlocked = () => {
    vm.actions.clearToast();
    setBlockToast(INSTALL_PERM_MSG);
  };

  // Admin curate controls for an installed row, by approval state.
  const renderCurateButtons = (s: InstalledSkill) => {
    if (!allowlistEnabled || !canCurate) return null;
    const busy = vm.curatingIds.has(s.id);
    if (s.approvalStatus === 'approved') {
      return (
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() => void vm.actions.revoke(s.id)}
          data-testid={`settings-skills-revoke-${s.id}`}
        >
          Revoke
        </Button>
      );
    }
    if (s.approvalStatus === 'blocked') {
      return (
        <Button
          variant="primary"
          disabled={busy}
          onClick={() => void vm.actions.approve(s.id)}
          data-testid={`settings-skills-approve-${s.id}`}
        >
          Approve
        </Button>
      );
    }
    // pending
    return (
      <>
        <Button
          variant="primary"
          disabled={busy}
          onClick={() => void vm.actions.approve(s.id)}
          data-testid={`settings-skills-approve-${s.id}`}
        >
          Approve
        </Button>
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() => void vm.actions.block(s.id)}
          data-testid={`settings-skills-block-${s.id}`}
        >
          Block
        </Button>
      </>
    );
  };

  return (
    <section
      className="set-sk-page"
      data-testid="settings-skills"
      data-status={vm.status}
      data-allowlist={allowlistEnabled ? 'on' : 'off'}
    >
      <SettingsBackLink />

      {vm.status === 'loading' ? (
        <div className="set-sk-loading" data-testid="settings-skills-loading">
          <Spinner size="lg" label="Loading skills" />
        </div>
      ) : vm.status === 'unauth' ? (
        <Banner kind="error" data-testid="settings-skills-unauth">
          Sign in to manage skills.
        </Banner>
      ) : (
        <>
          <header className="set-sk-head">
            <h1 className="set-sk-title">Skills</h1>
            <p className="set-sk-lede">{LEDE}</p>
          </header>

          {/* Install a skill */}
          <div className="set-sk-section">
            <SectionHead title="Install a skill" />

            {vm.status === 'error' && vm.error ? (
              <Banner kind="error" data-testid="settings-skills-error">
                {vm.error}
              </Banner>
            ) : null}
            {vm.installError ? (
              <Banner kind="error" data-testid="settings-skills-install-error">
                {vm.installError}
              </Banner>
            ) : null}

            <div className="set-sk-form" data-testid="settings-skills-form">
              <TextField
                id="settings-skills-url"
                label="GitHub repo URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="github.com/acme-org/agent-skills"
                disabled={vm.installing}
              />
              <TextField
                id="settings-skills-version"
                label="Version / ref (optional)"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="v2.1 — leave blank to install unpinned"
                disabled={vm.installing}
              />
              <TextField
                id="settings-skills-name"
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="defaults to the repo name"
                disabled={vm.installing}
              />
              <div className="set-sk-form-actions">
                {!canSubmitAdd && !capsLoading ? (
                  <span
                    className="set-sk-attrib set-sk-blocked-note"
                    data-testid="settings-skills-install-blocked"
                  >
                    <span className="set-sk-reason-ico" aria-hidden="true">
                      ⊘
                    </span>
                    {INSTALL_PERM_MSG}
                  </span>
                ) : (
                  <span className="set-sk-attrib" data-testid="settings-skills-add-note">
                    {addNote}
                  </span>
                )}
                <Button
                  variant="primary"
                  onClick={() => void onAdd()}
                  disabled={vm.installing || !url || capsLoading || !canSubmitAdd}
                  data-testid="settings-skills-install"
                >
                  {vm.installing ? busyLabel : addLabel}
                </Button>
              </div>
            </div>
          </div>

          {/* Governance posture (curator control / blocked-by-policy notice) */}
          {capsLoading ? null : (
            <div className="set-sk-section">
              <SectionHead title="Org governance" />
              <div
                className="set-sk-posture"
                data-testid="settings-skills-posture"
                data-disabled={canCurate ? 'false' : 'true'}
              >
                <span className="set-sk-posture-mark" aria-hidden="true">
                  ◆
                </span>
                <div className="set-sk-posture-text">
                  <div className="set-sk-posture-name">
                    Allowlist
                    <Chip variant={allowlistEnabled ? 'success' : 'neutral'}>
                      {allowlistEnabled ? 'ON' : 'OFF'}
                    </Chip>
                  </div>
                  <div className="set-sk-posture-desc">
                    {allowlistEnabled
                      ? 'Only approved skills are usable. Unapproved skills are blocked by org policy.'
                      : 'All installed skills are usable. Turn the allowlist on to govern.'}
                  </div>
                </div>
                <Switch
                  label="Enforce allowlist"
                  hideLabel
                  checked={allowlistEnabled}
                  disabled={!canCurate || vm.postureSaving}
                  onChange={(e) =>
                    void vm.actions.setAllowlistEnabled(e.target.checked)
                  }
                  data-testid="settings-skills-allowlist-toggle"
                />
              </div>
              {!canCurate ? (
                <div
                  className="set-sk-reason"
                  data-testid="settings-skills-curate-blocked"
                >
                  <span className="set-sk-reason-ico" aria-hidden="true">
                    ⊘
                  </span>
                  <span>
                    <b>Blocked by org policy</b> — needs the Manage allowlist
                    permission.{' '}
                    <span className="set-sk-reason-path">
                      {allowlistEnabled
                        ? 'Unapproved skills are blocked below; ask your org admin to approve one you need.'
                        : 'Ask your org admin to manage the allowlist.'}
                    </span>
                  </span>
                </div>
              ) : null}
            </div>
          )}

          {/* Requested review queue (only when the allowlist is on) */}
          {allowlistEnabled ? (
            <div className="set-sk-section">
              <SectionHead
                title="Requested"
                tag={String(vm.requested.length)}
              />
              {vm.requested.length === 0 ? (
                <EmptyState data-testid="settings-skills-requested-empty">
                  No pending requests.
                </EmptyState>
              ) : (
                <ul
                  className="set-sk-list"
                  data-testid="settings-skills-requested"
                >
                  {vm.requested.map((r) => (
                    <li
                      key={r.id}
                      className="set-sk-row"
                      data-req-id={r.id}
                    >
                      <div className="set-sk-row-main">
                        <div className="set-sk-row-top">
                          <span className="set-sk-row-name">{r.name}</span>
                        </div>
                        {r.repoUrl ? (
                          <a
                            className="set-sk-repo"
                            href={r.repoUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {r.repoUrl.replace(/^https?:\/\//, '')}
                            <span className="set-sk-repo-ext" aria-hidden="true">
                              ↗
                            </span>
                          </a>
                        ) : null}
                      </div>
                      <div className="set-sk-row-actions">
                        {canCurate ? (
                          <>
                            <Button
                              variant="primary"
                              disabled={vm.curatingIds.has(r.id)}
                              onClick={() => void vm.actions.approveRequest(r.id)}
                              data-testid={`settings-skills-req-approve-${r.id}`}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => void vm.actions.denyRequest(r.id)}
                              data-testid={`settings-skills-req-deny-${r.id}`}
                            >
                              Deny
                            </Button>
                          </>
                        ) : (
                          <Chip variant="warning">awaiting review</Chip>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {/* Installed */}
          <div className="set-sk-section">
            <SectionHead title="Installed" tag={String(vm.installed.length)} />

            {vm.installed.length === 0 ? (
              <EmptyState data-testid="settings-skills-empty">
                No skills yet. Install one from a GitHub repo above.
              </EmptyState>
            ) : (
              <ul className="set-sk-list" data-testid="settings-skills-list">
                {vm.installed.map((s) => {
                  const isUsable = vm.usable(s);
                  return (
                    <li
                      key={s.id}
                      className="set-sk-row"
                      data-skill-id={s.id}
                      data-enabled={isUsable && s.enabled ? 'true' : 'false'}
                      data-usable={isUsable ? 'true' : 'false'}
                      data-approval={s.approvalStatus}
                    >
                      <div className="set-sk-row-main">
                        <div className="set-sk-row-top">
                          <span className="set-sk-row-name">{s.name}</span>
                          <span className="set-sk-row-chips">
                            <Chip variant="neutral">{s.source}</Chip>
                            {s.pinnedVersion ? (
                              <Chip variant="info">{s.pinnedVersion}</Chip>
                            ) : (
                              <Chip variant="neutral">unpinned</Chip>
                            )}
                            {allowlistEnabled ? (
                              <ApprovalChip status={s.approvalStatus} />
                            ) : null}
                          </span>
                        </div>
                        {!isUsable ? (
                          <div
                            className="set-sk-reason"
                            data-testid={`settings-skills-reason-${s.id}`}
                          >
                            <span className="set-sk-reason-ico" aria-hidden="true">
                              ⊘
                            </span>
                            <span>
                              <b>Blocked by org policy</b> — not on your org's
                              allowlist.{' '}
                              <span className="set-sk-reason-path">
                                {canCurate
                                  ? 'Approve it to make it usable for every member.'
                                  : 'Ask your org admin to approve this skill.'}
                              </span>
                            </span>
                          </div>
                        ) : null}
                      </div>
                      <div className="set-sk-row-actions">
                        {renderCurateButtons(s)}
                        <span
                          className="set-sk-toggle-wrap"
                          data-testid={`settings-skills-toggle-${s.id}`}
                          onClick={
                            !isUsable
                              ? flagBlocked
                              : !canInstall
                                ? flagPermBlocked
                                : undefined
                          }
                        >
                          <Switch
                            label={`Enable ${s.name}`}
                            hideLabel
                            checked={isUsable && s.enabled}
                            disabled={!isUsable || !canInstall || vm.togglingIds.has(s.id)}
                            onChange={(e) =>
                              void vm.actions.toggle(s.id, e.target.checked)
                            }
                          />
                        </span>
                        <span
                          className="set-sk-remove-wrap"
                          onClick={canInstall ? undefined : flagPermBlocked}
                        >
                          <Button
                            variant="ghost"
                            disabled={!canInstall}
                            onClick={() => setPendingRemove(s)}
                            data-testid={`settings-skills-remove-${s.id}`}
                          >
                            Remove
                          </Button>
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}

      <Modal
        open={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        dataTestId="settings-skills-remove-confirm"
        title="Remove this skill?"
        footer={
          <>
            <Button
              type="button"
              variant="default"
              onClick={() => setPendingRemove(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              data-testid="settings-skills-remove-confirm-go"
              onClick={() => {
                const id = pendingRemove?.id;
                setPendingRemove(null);
                if (id) void vm.actions.uninstall(id);
              }}
            >
              Remove
            </Button>
          </>
        }
      >
        <p>
          <strong>{pendingRemove?.name}</strong> will be removed from this org's
          registry. This can't be undone — you'd need to re-install it.
        </p>
      </Modal>

      {toast ? <Toast onDismiss={dismissToast}>{toast}</Toast> : null}
    </section>
  );
}
